/**
 * Formatage des horodatages, en français, fuseau Europe/Paris.
 *
 * Toute la base est en epoch ms UTC ; le fuseau n'intervient qu'à l'affichage.
 * Les calculs de « hier » / « aujourd'hui » se font sur le calendrier
 * **parisien**, pas sur le calendrier UTC : à 00h30 heure de Paris en été, UTC
 * est encore la veille, et « hier » afficherait « aujourd'hui ».
 */

export const DISPLAY_TIME_ZONE = 'Europe/Paris';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('fr-FR', {
  timeZone: DISPLAY_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
});

const weekdayFormatter = new Intl.DateTimeFormat('fr-FR', {
  timeZone: DISPLAY_TIME_ZONE,
  weekday: 'long',
});

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  timeZone: DISPLAY_TIME_ZONE,
  day: 'numeric',
  month: 'long',
});

const stampFormatter = new Intl.DateTimeFormat('fr-FR', {
  timeZone: DISPLAY_TIME_ZONE,
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const fullFormatter = new Intl.DateTimeFormat('fr-FR', {
  timeZone: DISPLAY_TIME_ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Numéro de jour absolu dans le calendrier parisien, pour comparer deux dates. */
function parisDayNumber(ts: number): number {
  // en-CA produit « YYYY-MM-DD », le seul format ISO stable via Intl.
  const [year, month, day] = dayFormatter.format(new Date(ts)).split('-').map(Number);
  return Date.UTC(year as number, (month as number) - 1, day as number) / 86_400_000;
}

/** « 18h32 » — l'usage français, plutôt que le « 18:32 » rendu par Intl. */
function parisTime(ts: number): string {
  return timeFormatter.format(new Date(ts)).replace(':', 'h');
}

/**
 * Horodatage relatif court, affiché sous chaque article.
 *
 * « à l'instant » · « il y a 5 min » · « il y a 3 h » · « hier 18h32 »
 * · « mardi 18h32 » · « 12 mars » · « 12 mars 2025 »
 */
export function formatRelative(ts: number, now: number = Date.now()): string {
  const delta = now - ts;

  // Une horloge client en avance ne doit pas produire « il y a -3 min ».
  if (delta < 0) return 'à l’instant';
  if (delta < MINUTE) return 'à l’instant';

  if (delta < HOUR) {
    const minutes = Math.floor(delta / MINUTE);
    return `il y a ${minutes} min`;
  }

  const dayDelta = parisDayNumber(now) - parisDayNumber(ts);

  if (dayDelta === 0) {
    const hours = Math.floor(delta / HOUR);
    return `il y a ${hours} h`;
  }
  if (dayDelta === 1) return `hier ${parisTime(ts)}`;
  if (dayDelta < 7) return `${weekdayFormatter.format(new Date(ts))} ${parisTime(ts)}`;

  const sameYear = new Date(ts).getUTCFullYear() === new Date(now).getUTCFullYear();
  const date = dateFormatter.format(new Date(ts));
  return sameYear ? date : `${date} ${new Date(ts).getUTCFullYear()}`;
}

/** Valeur absolue, affichée au tap sur l'horodatage relatif. */
export function formatAbsolute(ts: number): string {
  return fullFormatter.format(new Date(ts)).replace(':', 'h');
}

/**
 * Date et heure sans jour de la semaine : « 23 août 2026 à 23h11 ».
 *
 * Pour un horodatage qu'on lit une fois — la date d'un build, par exemple —
 * « dimanche » n'apporte rien et allonge la ligne.
 */
export function formatStamp(ts: number): string {
  return stampFormatter.format(new Date(ts)).replace(':', 'h');
}
